/**
 * Analytics façade — call these from anywhere in the app.
 *
 * Every function is a silent no-op when PostHog is disabled (no key)
 * or before `initPostHog()` has run. This keeps call-sites clean: no
 * `if (enabled)` guard needed at every funnel event.
 *
 * Funnel event naming convention:
 *   <surface>_<action>     e.g. freescan_started, pricing_viewed,
 *                                audit_submitted, verify_loaded
 *
 * All events route through the named `qortrace` PostHog instance —
 * isolated from Emergent platform telemetry. See lib/posthog.js.
 */
import { getInstance, isPostHogEnabled } from "./posthog";


// Events we mirror to /api/public/labs/events for first-party
// capture in BOTH preview (where PostHog is unconfigured) and
// production (where PostHog is also active). The backend keeps the
// authoritative copy that drives the admin Methodology-funnel tile.
const FIRST_PARTY_EVENTS = new Set([
  "hero_read_toggle_opened",
  "hero_read_toggle_closed",
  "tour_started",
  "tour_step_viewed",
  "tour_completed",
  "tour_dismissed",
  "verify_share_click",
  "verify_landed_from_share",
  "verify_share_consult_auto_opened",
  "verify_final_cta_click",
]);

function mirrorToFirstParty(name, properties) {
  if (!FIRST_PARTY_EVENTS.has(name)) return;
  if (typeof window === "undefined") return;
  // Keepalive lets the request survive the 'visibilitychange→hidden'
  // event that happens when the user clicks a target=_blank share
  // link.  Fetch + keepalive is the canonical pattern for "fire-
  // and-forget on unload" telemetry.
  const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/labs/events`;
  const body = JSON.stringify({ name, props: properties || {} });
  try {
    window.fetch(url, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      // First-party capture is anonymous + non-essential — fall
      // through silently on any network blip.
      mode:        "cors",
      credentials: "omit",
    }).catch(() => undefined);
  } catch {
    // navigator.sendBeacon would be an even cleaner fallback but it
    // doesn't ship CORS-preflight semantics and Mongo wants JSON.
  }
}


function safe(fn) {
  const ph = getInstance();
  if (!ph) return;
  try {
    fn(ph);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[analytics] call failed:", err);
  }
}

export function trackEvent(name, properties = {}) {
  if (!name) return;
  // First-party capture runs UNCONDITIONALLY for whitelisted events
  // (PostHog may or may not be enabled — we always want the backend
  // copy).
  mirrorToFirstParty(name, properties);
  // PostHog call — silent no-op if disabled.
  safe((ph) => ph.capture(name, properties));
}

export function trackPageView(path, properties = {}) {
  safe((ph) =>
    ph.capture("$pageview", {
      $current_url: typeof window !== "undefined" ? window.location.href : path,
      path,
      ...properties,
    })
  );
}

export function identifyUser(distinctId, properties = {}) {
  if (!distinctId) return;
  safe((ph) => ph.identify(distinctId, properties));
}

export function resetUser() {
  safe((ph) => ph.reset());
}

export { isPostHogEnabled };
